'use client';

import React, {
  type ButtonHTMLAttributes,
  type FC,
  type PropsWithChildren,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from 'styled-components';
import { IconComponent } from '@/components/IconComponent';
import type { WithTestId } from '@/helpers/testIds';
import useEventListener from '@/hooks/eventListener';
import { useRegModalTheme } from '@/modules/registration';
import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import {
  ScButton,
  type ScButtonProps,
  ScButtonWrapper,
  ScLink,
} from './styled';

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    WithTestId {
  target?: string;
  href?: string;
  to?: string;
  linkScroll?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  navigation?: boolean;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface IHandleLinkWrappingProps {
  Component: React.ComponentType<any>;
  props: PropsWithChildren<ButtonProps>;
}

const HandleLinkWrapping = React.memo(
  ({ Component, props }: IHandleLinkWrappingProps) => {
    const {
      href,
      to,
      linkScroll = false,
      target,
      children,
      disabled,
      onLinkClick,
      navigation,
      testId,
      type = 'button',
      ...rest
    } = props;

    const button = (
      <Component
        data-testid={testId}
        disabled={disabled || rest.isLoading}
        type={type}
        {...rest}
      >
        <span className="text">{children}</span>
      </Component>
    );

    if (href) {
      return (
        <a
          className="button-link"
          href={href}
          rel={target ? undefined : 'noopener noreferrer'}
          target={target || '_blank'}
        >
          {button}
        </a>
      );
    }

    if (navigation) {
      return (
        <ScButtonWrapper>
          {button}
          <IconComponent
            className="blue-arrow-icon"
            name="whiteArrowRight"
            size="18px"
          />
        </ScButtonWrapper>
      );
    }

    if (to)
      return (
        <ScLink href={to} onClick={onLinkClick} scroll={linkScroll}>
          {button}
        </ScLink>
      );

    return button;
  }
);

const ButtonComp: FC<
  Omit<ScButtonProps, 'isMobileDevice'> & {
    children: React.ReactNode;
    isLoading?: boolean;
  }
> = ({ children, isLoading = false, ...props }) => {
  const {
    primary,
    primaryTransparent,
    secondary,
    secondaryTransparent,
    blackTransparent,
    white,
    secondaryGreenGradient,
  } = props;

  const { mainColor, whiteColor, secondaryColor, blackColor, greenColor } =
    useTheme();
  const { buttons } = useRegModalTheme();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const ref = useRef<HTMLButtonElement>(null);

  useEventListener(
    'mouseenter',
    (e) => {
      if (!ref.current) return;
      const relX = e.pageX - ref.current.getBoundingClientRect().left;
      const relY = e.clientY - ref.current.getBoundingClientRect().top;

      const element = ref.current.querySelector('div.aware') as HTMLDivElement;

      if (!element) return;
      element.style.top = `${relY}px`;
      element.style.left = `${relX}px`;
    },
    ref
  );

  const styleSpinner = useMemo(() => {
    switch (true) {
      case primary != null:
        return { color: whiteColor, background: mainColor };
      case secondary != null:
        return { color: whiteColor, background: secondaryColor };
      case secondaryTransparent != null:
        return { color: secondaryColor, background: blackColor };
      case blackTransparent != null:
        return { color: blackColor, background: whiteColor };
      case white != null:
        return { color: blackColor, background: whiteColor };
      case secondaryGreenGradient != null:
        return { color: whiteColor, background: greenColor };
      default:
        return { color: whiteColor, background: blackColor };
    }
  }, [
    primary,
    secondary,
    secondaryTransparent,
    blackTransparent,
    secondaryGreenGradient,
    white,
    mainColor,
    whiteColor,
    secondaryColor,
    blackColor,
    greenColor,
  ]);
  return (
    <ScButton
      $buttons={buttons}
      isMobileDevice={isMobileDevice}
      ref={ref}
      {...props}
    >
      {primaryTransparent && <span className="bg" />}
      {isLoading ? <Spinner size={20} {...styleSpinner} center /> : children}
      <span className="awareWrap">
        <div className="aware" />
      </span>
    </ScButton>
  );
};

export const Button: FC<
  ButtonProps &
    Omit<ScButtonProps, 'isMobileDevice'> & {
      component?: React.ComponentType<ButtonProps>;
    } & ButtonHTMLAttributes<HTMLButtonElement>
> = ({ component, ...rest }) => (
  <HandleLinkWrapping Component={component || ButtonComp} props={rest} />
);
